/**
 * Marketing / Ads query keys. Canonical tags omit them (see `absoluteUrl`),
 * but the document URL must keep them: a 301 that drops `gclid` / `utm_*`
 * runs before gtag and collapses Google Ads into google/organic.
 * Affiliate click ids stay in this list's complement so they are never
 * treated as disposable tracking keys.
 */

export const ATTRIBUTION_BRIDGE_COOKIE = 'qd_attr_bridge:v1';

/** Cookie TTL: long enough for the 301 hop + first paint hydration. */
export const ATTRIBUTION_BRIDGE_MAX_AGE_SEC = 600;

const EXACT_TRACKING_KEYS = new Set([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'utm_creative',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'msclkid',
  '_ga',
  'mc_cid',
  'mc_eid',
]);

const TRACKING_KEY_PREFIXES = ['utm_', 'gad_'] as const;

const STATIC_FILE_SEGMENT =
  /\.(xml|txt|pdf|csv|svg|png|jpe?g|gif|ico|json|webmanifest|woff2?|m?js|cjs|css|map)$/i;

export type AttributionBridgePayload = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  msclkid?: string;
  gad_source?: string;
};

export function isTrackingParam(key: string): boolean {
  const lower = key.toLowerCase();
  if (EXACT_TRACKING_KEYS.has(lower)) return true;
  return TRACKING_KEY_PREFIXES.some((prefix) => lower.startsWith(prefix));
}

export function hasTrackingParams(
  searchParams: URLSearchParams | Iterable<[string, string]>,
): boolean {
  for (const [key] of searchParams) {
    if (isTrackingParam(key)) return true;
  }
  return false;
}

/** Returns a new URLSearchParams without tracking keys; original is unchanged. */
export function stripTrackingParams(
  searchParams: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams();
  for (const [key, value] of searchParams) {
    if (!isTrackingParam(key)) {
      next.append(key, value);
    }
  }
  return next;
}

function pickTrimmed(
  params: URLSearchParams,
  key: string,
): string | undefined {
  const value = params.get(key);
  if (!value?.trim()) return undefined;
  return value.trim().slice(0, 255);
}

/** Snapshot of stripped marketing params for the client attribution bridge. */
export function extractAttributionBridgePayload(
  searchParams: URLSearchParams,
): AttributionBridgePayload | null {
  const payload: AttributionBridgePayload = {
    utm_source: pickTrimmed(searchParams, 'utm_source'),
    utm_medium: pickTrimmed(searchParams, 'utm_medium'),
    utm_campaign: pickTrimmed(searchParams, 'utm_campaign'),
    utm_term: pickTrimmed(searchParams, 'utm_term'),
    utm_content: pickTrimmed(searchParams, 'utm_content'),
    gclid: pickTrimmed(searchParams, 'gclid'),
    gbraid: pickTrimmed(searchParams, 'gbraid'),
    wbraid: pickTrimmed(searchParams, 'wbraid'),
    fbclid: pickTrimmed(searchParams, 'fbclid'),
    msclkid: pickTrimmed(searchParams, 'msclkid'),
    gad_source: pickTrimmed(searchParams, 'gad_source'),
  };

  const hasValue = Object.values(payload).some(Boolean);
  return hasValue ? payload : null;
}

export function serializeAttributionBridgeCookie(
  payload: AttributionBridgePayload,
): string {
  // Plain JSON — NextResponse.cookies / js-cookie handle encoding.
  // Do not encodeURIComponent here or the value is double-encoded.
  return JSON.stringify(payload);
}

export function parseAttributionBridgeCookie(
  raw: string | undefined | null,
): AttributionBridgePayload | null {
  if (!raw?.trim()) return null;
  try {
    const trimmed = raw.trim();
    // Prefer raw JSON; fall back to decode for any legacy double-encoded cookie.
    const candidates = [trimmed];
    try {
      candidates.push(decodeURIComponent(trimmed));
    } catch {
      // ignore malformed % sequences
    }
    for (const candidate of candidates) {
      try {
        const parsed = JSON.parse(candidate) as AttributionBridgePayload;
        if (parsed && typeof parsed === 'object') return parsed;
      } catch {
        // try next candidate
      }
    }
    return null;
  } catch {
    return null;
  }
}

export function isStaticFilePath(pathname: string): boolean {
  const last = pathname.split('/').filter(Boolean).pop() ?? '';
  return STATIC_FILE_SEGMENT.test(last);
}

/**
 * Paths that should end with `/` under `trailingSlash: true`, excluding
 * file-like segments (sitemap xml, pdf, etc.).
 */
export function needsTrailingSlash(pathname: string): boolean {
  if (!pathname || pathname === '/') return false;
  if (pathname.endsWith('/')) return false;
  if (isStaticFilePath(pathname)) return false;
  return true;
}

export function normalizeSlugForCompare(slug: string): string {
  return String(slug ?? '').normalize('NFC');
}

/**
 * Build pathname + search for a host / trailing-slash redirect.
 * The query string is copied whole, including `gclid`, `gad_*` and `utm_*`.
 */
export function buildCleanUrlParts(input: {
  pathname: string;
  searchParams: URLSearchParams;
}): {
  pathname: string;
  search: string;
  strippedTracking: boolean;
  addedTrailingSlash: boolean;
  bridgePayload: AttributionBridgePayload | null;
} {
  const addedTrailingSlash = needsTrailingSlash(input.pathname);
  const pathname = addedTrailingSlash
    ? `${input.pathname}/`
    : input.pathname;

  const search = input.searchParams.toString();

  return {
    pathname,
    search: search ? `?${search}` : '',
    strippedTracking: false,
    addedTrailingSlash,
    bridgePayload: null,
  };
}
